import React from 'react';
import { 
  BookOpen, 
  Clock, 
  Calendar, 
  ArrowLeft, 
  Sparkles, 
  User, 
  ChevronLeft,
  Share2,
  Tag
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useLanguage } from '../../context/LanguageContext';

const BlogSection = ({ onSelectArticle, onViewAllArticles }) => {
  const { blogs = [], settings = {} } = useStore();
  const { lang, isRtl } = useLanguage();

  // Get published articles, sorted by creation date or feature
  const publishedArticles = blogs
    .filter(b => b.status === 'published')
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // Display top 3-4 articles on home
  const displayArticles = publishedArticles.slice(0, 3);

  if (displayArticles.length === 0) {
    return null;
  }

  return (
    <section className="py-12 md:py-16 bg-gradient-to-b from-amber-50/40 via-white to-emerald-50/30 border-t border-b border-emerald-900/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 pb-4 border-b border-emerald-800/15 gap-4">
          <div className="text-start">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/80 border border-emerald-200 text-emerald-900 text-xs font-bold mb-2 font-urdu">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span>{lang === 'ur' ? 'مستند طبی مقالات و صحت و غذائیت گائیڈ' : 'Tibbi Articles & Health Guides'}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-emerald-950 font-title tracking-tight">
              {lang === 'ur' ? 'صحت، جڑی بوٹیاں اور غذا کے خاص مضامین' : 'Articles on Health, Botanicals & Nutrition'}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 font-urdu mt-1 max-w-2xl">
              {lang === 'ur'
                ? 'حکیم مولانا سید عبدالوہاب شاہ شیرازی کی رہنمائی میں قدیم طب نبوی، طب یونانی اور جدید سائنسی تحقیق پر مبنی مستند مضامین کا مطالعہ کریں۔'
                : 'Explore research-backed articles on traditional herbal remedies, dietetics, and holistic health.'}
            </p>
          </div>

          <button
            type="button"
            onClick={onViewAllArticles}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs sm:text-sm shadow-md transition-all hover:gap-3 cursor-pointer self-start md:self-auto font-urdu"
          >
            <span>{lang === 'ur' ? `تمام مضامین پڑھیں (${publishedArticles.length})` : `Read All Articles (${publishedArticles.length})`}</span>
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>

        {/* Article Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {displayArticles.map((article) => (
            <article 
              key={article.id}
              onClick={() => onSelectArticle(article)}
              className="group bg-white rounded-2xl sm:rounded-3xl overflow-hidden border border-emerald-900/10 hover:border-emerald-600/40 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer transform hover:-translate-y-1"
            >
              {/* Image Container */}
              <div className="relative aspect-[16/10] overflow-hidden bg-emerald-950">
                <img
                  src={article.coverImage || article.featuredImage || "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80"}
                  alt={article.titleUrdu || article.title}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { 
                    e.currentTarget.onerror = null; 
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&auto=format&fit=crop&q=80'; 
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
                
                {/* Category Badge */}
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-900/90 backdrop-blur-xs text-emerald-100 text-[11px] font-bold border border-emerald-700/50 shadow-sm font-urdu">
                    <Tag className="w-3 h-3 text-amber-400" />
                    <span>{lang === 'ur' ? (article.categoryUrdu || article.category || 'جڑی بوٹیاں') : (article.category || 'Herbs')}</span>
                  </span>
                </div>

                {/* Read Time */}
                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 text-white/90 text-xs font-urdu bg-black/50 backdrop-blur-xs px-2.5 py-0.5 rounded-lg">
                  <Clock className="w-3.5 h-3.5 text-emerald-300" />
                  <span>{lang === 'ur' ? (article.readTime || '5 منٹ کا مطالعہ') : (article.readTimeEn || '5 min read')}</span>
                </div>
              </div>

              {/* Content Box */}
              <div className="p-5 sm:p-6 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-2.5 text-start">
                  <div className="flex items-center justify-between text-xs text-gray-500 font-urdu border-b border-gray-100 pb-2">
                    <span className="flex items-center gap-1 text-emerald-800 font-bold">
                      <User className="w-3.5 h-3.5" />
                      <span>{lang === 'ur' ? (article.author || settings.hakimNameUrdu) : (article.authorEn || settings.hakimNameEn)}</span>
                    </span>
                    <span className="flex items-center gap-1 text-gray-400 font-sans text-[11px]">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{article.publishedDate || article.createdAt}</span>
                    </span>
                  </div>

                  <h3 className="font-bold text-base sm:text-lg text-gray-900 font-title leading-snug group-hover:text-emerald-800 transition-colors line-clamp-2">
                    {lang === 'ur' ? (article.titleUrdu || article.title) : (article.titleEn || article.title)}
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-600 font-urdu leading-relaxed line-clamp-3">
                    {lang === 'ur' ? (article.excerptUrdu || article.excerpt) : (article.excerptEn || article.excerpt)}
                  </p>
                </div>

                {/* Bottom Card Action */}
                <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-700 font-urdu flex items-center gap-1 group-hover:text-emerald-900 transition-colors">
                    <span>{lang === 'ur' ? 'مکمل مضمون پڑھیں' : 'Read Full Article'}</span>
                    <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                  </span>

                  {(article.tags?.[0] || article.focusKeywords) && (
                    <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-100 font-urdu hidden sm:inline-block">
                      {article.tags?.[0] || article.focusKeywords?.split(',')[0]}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View All Mobile Button */}
        <div className="mt-8 text-center md:hidden">
          <button
            type="button"
            onClick={onViewAllArticles}
            className="w-full py-3 px-6 rounded-xl bg-emerald-800 text-white font-bold text-sm shadow-md font-urdu flex items-center justify-center gap-2 cursor-pointer"
          >
            <span>{lang === 'ur' ? `تمام طبی مضامین و بلاگ لائبریری دیکھیں (${publishedArticles.length})` : `Explore All Articles (${publishedArticles.length})`}</span>
            <ArrowLeft className="w-4 h-4" />
          </button>
        </div>

      </div>
    </section>
  );
};

export default BlogSection;
